// QBR — single-module FMX fetcher.
//
// Given a project, a work-request module slug, and a date range, this pulls
// the WOs for that module that fall in the range and returns the raw inputs
// that QbrDataStep cards expect. It's intentionally defensive: FMX response
// shapes vary by tenant / module, so when an expected field is missing we
// return null for the affected metric rather than fabricating a number.
//
// Pagination strategy: filter server-side with BOTH ends of the window
// bounded (createdFromDate/createdToDate for work requests;
// dueFromDate/dueToDate for PM occurrences, which filter the scheduled
// `date` column) and walk pages with an explicit `fields=` list so pages
// stay small. The upper bound matters twice over: FMX's default WR sort
// is ID descending, so rows created mid-pagination would otherwise land
// at offset 0 and shift every later page (dup/skip), and PM occurrences
// are generated ahead of time, so an unbounded pull drags in future
// scheduled work. Rows are also deduped by id as a belt-and-braces
// guard. The walk is still capped so a runaway result set can't loop
// forever; each paginated pull reads the FMX-Total-Count response header
// and reports a truncation warning if the cap cut it short.
//
// FMX read rate limit is 1000 requests per user per 10-minute window —
// large pages + bulk-id fetches keep a full QBR pull well under it.

// QBR reads go through the shared concurrency gate (qbrFmxFetch) rather than
// apiClient's fmxFetch directly, so the whole QBR pull stays under one global
// in-flight ceiling against the customer's FMX site. Aliased to fmxFetch so
// the call sites below read unchanged.
import { qbrFmxFetch as fmxFetch, fmxErrorText } from './fmxThrottle.js';
import { fetchAdoptionData } from './fmxUsers.js';

const PAGE_LIMIT = 500; // FMX enforces no limit clamp; large pages cut round trips
const MAX_PAGES  = 200; // safety cap → up to 100k rows in one module pull
const DAY_MS     = 24 * 3600 * 1000;

// Module-qualified work-request endpoints follow the pattern /v1/<slug>-requests.
// e.g. moduleSlug = "maintenance" → /v1/maintenance-requests.
function workRequestEndpoint(slug) {
  return `/v1/${slug}-requests`;
}

// PM occurrences endpoint: /v1/<slug>/occurrences. NOT /v1/<slug>/tasks —
// that one returns the recurring PM *templates* (definitions), whereas
// QBR metrics need the actual scheduled-and-completed instances in the
// quarter. Endpoint path is the parallel to the /tasks template list.
function workTaskOccurrencesEndpoint(slug) {
  return `/v1/${slug}/occurrences`;
}

// Every pull passes an explicit fields= list (WR_FIELDS / OCC_FIELDS /
// invoice fields). FMX silently ignores unknown field names, so the lists
// are safe across tenants; the timestamp probers below still try fallback
// names in case a tenant's schema differs from the canonical read models.

// Auto-pull every configured module on the project. A "scope" is a unique
// slug across both module types — if a slug has both a work-request and
// a work-task module (the conventional FMX pairing, e.g. maintenance-
// requests ↔ maintenance/occurrences) we pull both into the same scope.
// Otherwise the scope holds whichever side exists. Adoption is global so
// it runs once across all users.
//
// Returns:
//   {
//     byModule: { [slug]: {
//       moduleSlug, moduleLabel, hasWR, hasWT,
//       rawCounts, metrics, fieldsSeen, sampleKeys, ...
//     } },
//     overall:  { rawCounts, metrics },
//     adoption: { active30Pct, active90Pct, mobilePct: null } | null,
//     adoptionDebug, moduleSlugs, errors: { [slug]: errorMessage }
//   }
export async function fetchAllModulesQbrData({
  projectId, workRequestModules, workTaskModules, scheduleRequestModules,
  pmTargetPct, powerUserTypeIds,
  periodStart, periodEnd,
}) {
  const wrBySlug = new Map((workRequestModules || []).map(m => [m.slug, m]));
  const wtBySlug = new Map((workTaskModules    || []).map(m => [m.slug, m]));
  const allSlugs = new Set([...wrBySlug.keys(), ...wtBySlug.keys()]);

  // Stable ordering: the WR modules' order (as configured) wins, then any
  // WT-only modules appended at the end.
  const scopes = [];
  for (const wr of workRequestModules || []) {
    scopes.push({
      slug: wr.slug,
      label: wr.label,
      wrModule: wr,
      wtModule: wtBySlug.get(wr.slug) || null,
    });
  }
  for (const wt of workTaskModules || []) {
    if (!wrBySlug.has(wt.slug)) {
      scopes.push({
        slug: wt.slug,
        label: wt.label,
        wrModule: null,
        wtModule: wt,
      });
    }
  }

  // Truncation/partial-data warnings keyed like `errors`. Every paginated
  // or capped pull compares FMX-Total-Count against what it actually got
  // and reports here so the debug panel can show that metrics were
  // computed on a partial set instead of silently undercounting.
  const warnings = {};
  const warnFor = key => msg => { warnings[key] = msg; };

  // Building lookup (id → { id, name }) shared across all scopes. Used
  // by the "Top buildings" (slide 8) and "Top overdue PMs" (slide 11)
  // aggregations to render names instead of raw IDs. Cheap — one
  // /v1/buildings pull, cached in-memory for the request.
  const buildingLookup = await fetchBuildingsLookup(projectId, warnFor('buildings')).catch(() => new Map());

  // Equipment lookup (id → { name, buildingID }) for slide 14 Asset
  // Health. Same one-shot pattern as buildings.
  const equipmentLookup = await fetchEquipmentLookup(projectId, warnFor('equipment')).catch(() => new Map());

  // Prior-quarter summary runs in parallel with the current pulls.
  // Returns response/resolution times + adoption %s anchored to the
  // prior period — enough to fill the "Q? vs Q? (prior)" columns on
  // slides 9 + 13 without doubling the round-trip cost of a full pull.
  const priorRange = computePriorQuarterRange(periodStart);
  const priorPromise = priorRange
    ? fetchPriorQuarterSummary({
        projectId, workRequestModules: workRequestModules || [],
        periodStart: priorRange.periodStart, periodEnd: priorRange.periodEnd,
        quarterLabel: priorRange.quarterLabel,
      }).catch(e => ({ error: e?.message || String(e) }))
    : Promise.resolve(null);

  const pulls = scopes.map(s => pullOneScope({
    projectId, scope: s, pmTargetPct, periodStart, periodEnd,
    buildingLookup, equipmentLookup, warn: warnFor(s.slug),
  }));
  const settled = await Promise.allSettled(pulls);

  const byModule = {};
  const errors = {};
  settled.forEach((s, i) => {
    const slug = scopes[i].slug;
    if (s.status === 'fulfilled') byModule[slug] = s.value;
    else errors[slug] = s.reason?.message || String(s.reason);
  });
  // unused — kept as a stable signal for whichever caller wants the set
  allSlugs.size;

  // Adoption — once, global. Wrapped so a 401/etc. on /users doesn't
  // tank the whole fetch. Returns BOTH an overall variant (every
  // eligible user) and a power-user variant (eligible AND userTypeID
  // ∈ powerUserTypeIds) when the FMXer made a selection on the Setup
  // step.
  let adoption = null;
  let adoptionPower = null;
  let adoptionDebug = null;
  try {
    const a = await fetchAdoptionData({ projectId, periodEnd, powerUserTypeIds });
    adoptionDebug = a;
    if (a.activePct30 != null || a.activePct90 != null) {
      adoption = {
        active30Pct: a.activePct30,
        active90Pct: a.activePct90,
        mobilePct: null,
      };
    }
    if (a.powerUser && (a.powerUser.activePct30 != null || a.powerUser.activePct90 != null)) {
      adoptionPower = {
        active30Pct: a.powerUser.activePct30,
        active90Pct: a.powerUser.activePct90,
        mobilePct: null,
      };
    }
  } catch (e) {
    adoptionDebug = { error: e.message };
  }

  // Per-user lookup for the Top Requestors aggregation (slide 13). The
  // adoption fetch already pulled /v1/users; reuse that list rather
  // than burning a second round-trip.
  const userLookup = new Map();
  for (const u of adoptionDebug?.eligibleUsers || []) {
    if (u?.id != null) userLookup.set(u.id, u);
  }

  // With the user lookup in hand, hydrate the per-scope topRequestors
  // entries with names. (pullOneScope only knows about IDs and counts —
  // the name resolution happens once here at the top level.)
  for (const m of Object.values(byModule)) {
    if (Array.isArray(m.topRequestors)) {
      m.topRequestors = m.topRequestors.map(r => {
        const u = userLookup.get(r.userID);
        // UserReadModel exposes building access as `accessibleBuildingIDs`
        // (there is no `buildingIDs` property) — first entry is the closest
        // available "home building" signal.
        const bId = Array.isArray(u?.accessibleBuildingIDs) ? u.accessibleBuildingIDs[0] : null;
        const location = bId != null ? (buildingLookup.get(bId)?.name || '') : '';
        return {
          ...r,
          name: u?.name || `User ${r.userID}`,
          location,
        };
      });
    }
  }

  const overall = aggregateModules(byModule, { pmTargetPct, adoption, adoptionPower });
  const prior = await priorPromise;

  // 4-quarter planned-vs-reactive trend for slide 10. Pulls 12 months
  // of WRs + PMs in one go, buckets client-side. Caught and tucked
  // into the result as `trend` — generation works without it.
  const trend = await fetchTrendData({
    projectId,
    workRequestModules: workRequestModules || [],
    workTaskModules:    workTaskModules    || [],
    currentPeriodStart: periodStart,
    quartersBack: 4,
    warn: warnFor('trend'),
  }).catch(e => ({ error: e?.message || String(e), quarters: [] }));

  // Slide 15 — Other Modules in Action. Scheduling event count across
  // every schedule-request module + invoice $ across /v1/invoices. Both
  // are best-effort: a failure leaves the field blank, em-dash on the
  // deck. Run in parallel with each other (and the other top-level
  // pulls would be too, but they've already awaited above).
  const [scheduling, invoicing] = await Promise.all([
    fetchSchedulingSummary({
      projectId,
      scheduleRequestModules: scheduleRequestModules || [],
      periodStart, periodEnd,
    }).catch(e => ({ error: e?.message || String(e) })),
    fetchInvoicingSummary({ projectId, periodStart, periodEnd, warn: warnFor('invoicing') })
      .catch(e => ({ error: e?.message || String(e) })),
  ]);

  // Surface the adoption pull's truncation (if any) alongside the rest.
  if (adoptionDebug?.truncationWarning) warnings.users = adoptionDebug.truncationWarning;

  return {
    byModule,
    overall,
    adoption,
    adoptionPower,     // { active30Pct, active90Pct, mobilePct } | null
    adoptionDebug,
    prior,             // { quarterLabel, response, adoption } or null
    trend,             // { quarters: [{quarterLabel, shortLabel, plannedPct}], error? }
    scheduling,        // { eventsInPeriod, error? }
    invoicing,         // { totalDollars, invoiceCount, error? }
    buildingLookup,    // exposed for downstream code that wants names
    userLookup,
    moduleSlugs: scopes.map(s => s.slug),
    moduleLabels: Object.fromEntries(scopes.map(s => [s.slug, s.label])),
    errors,
    warnings,          // { [key]: message } — truncated/partial pulls
  };
}

// Compute the prior calendar quarter's date range + label from the
// current quarter's start. Q4 2025 (Oct-Dec 2025) → Q3 2025 (Jul-Sep
// 2025). Returns null if the input doesn't parse.
function computePriorQuarterRange(periodStart) {
  const d = new Date(periodStart);
  if (Number.isNaN(d.getTime())) return null;
  // Subtract 3 months from the start date. Day stays at 1 since
  // calendar quarters start on the 1st.
  const priorStart = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 3, 1));
  // End = last day of the month two months after priorStart.
  const priorEnd = new Date(Date.UTC(priorStart.getUTCFullYear(), priorStart.getUTCMonth() + 3, 0));
  const fmt = (date) => date.toISOString().slice(0, 10);
  const q = Math.floor(priorStart.getUTCMonth() / 3) + 1;
  return {
    periodStart: fmt(priorStart),
    periodEnd: fmt(priorEnd),
    quarterLabel: `Q${q} ${priorStart.getUTCFullYear()}`,
    shortLabel:   `Q${q} '${String(priorStart.getUTCFullYear()).slice(-2)}`,
  };
}

// 4-quarter (or N-quarter) planned-vs-reactive trend for slide 10's
// stacked bar chart. One 12-month pull across each WR module + each
// WT module, then bucket the rows by quarter client-side. Per-quarter
// planned% = completedPMs / (completedPMs + completedWRs).
//
// Cheaper than 4 separate per-quarter pulls because pagination is
// shared. The trade-off is we pull every row in the window even if
// the customer's first quarter is empty.
async function fetchTrendData({ projectId, workRequestModules, workTaskModules, currentPeriodStart, quartersBack = 4, warn = null }) {
  // Compute the N quarter ranges, newest → oldest. quarters[0] is the
  // current quarter (caller passed currentPeriodStart).
  const quarters = [];
  let cursor = currentPeriodStart;
  for (let i = 0; i < quartersBack; i++) {
    const q = (i === 0)
      ? computeQuarterFromStart(cursor)
      : computePriorQuarterRange(cursor);
    if (!q) break;
    quarters.push(q);
    cursor = q.periodStart;
  }
  if (quarters.length === 0) return { quarters: [] };

  const oldest = quarters[quarters.length - 1];
  const trendStartMs = Date.parse(oldest.periodStart);
  const trendEndMs   = Date.parse(quarters[0].periodEnd) + 24 * 3600 * 1000 - 1;

  const wrPulls = workRequestModules.map(m =>
    paginateCreatedSince({
      projectId, endpoint: workRequestEndpoint(m.slug), startMs: trendStartMs, endMs: trendEndMs, warn,
    }).catch(() => [])
  );
  const pmPulls = workTaskModules.map(m =>
    paginateOccurrences({
      projectId, endpoint: workTaskOccurrencesEndpoint(m.slug), fromMs: trendStartMs, toMs: trendEndMs, warn,
    }).catch(() => [])
  );
  const [wrLists, pmLists] = await Promise.all([
    Promise.all(wrPulls),
    Promise.all(pmPulls),
  ]);
  const allWrs = wrLists.flat();
  const allPms = pmLists.flat();

  // Bucket each row into its quarter (or drop it if outside the window).
  const bucketed = quarters.map(() => ({ completedWrs: 0, completedPms: 0 }));
  for (const wo of allWrs) {
    const t = woCreatedMs(wo);
    if (t == null || t < trendStartMs || t > trendEndMs) continue;
    if (!woIsCompleted(wo)) continue;
    const idx = quarters.findIndex(q =>
      t >= Date.parse(q.periodStart) &&
      t <= Date.parse(q.periodEnd) + 24 * 3600 * 1000 - 1
    );
    if (idx >= 0) bucketed[idx].completedWrs++;
  }
  for (const pm of allPms) {
    const t = pmOccurrenceAnchorMs(pm);
    if (t == null || t < trendStartMs || t > trendEndMs) continue;
    if (!pmIsCompleted(pm)) continue;
    const idx = quarters.findIndex(q =>
      t >= Date.parse(q.periodStart) &&
      t <= Date.parse(q.periodEnd) + 24 * 3600 * 1000 - 1
    );
    if (idx >= 0) bucketed[idx].completedPms++;
  }

  // Return oldest-first so the deck (which renders left-to-right
  // chronologically) reads naturally.
  return {
    quarters: quarters.slice().reverse().map((q, revIdx) => {
      const bucketIdx = quarters.length - 1 - revIdx;
      const b = bucketed[bucketIdx];
      const total = b.completedWrs + b.completedPms;
      return {
        quarterLabel: q.quarterLabel,
        shortLabel:   q.shortLabel,
        plannedPct:   total > 0 ? round1((b.completedPms / total) * 100) : null,
      };
    }),
  };
}

// Treats `periodStart` as the start of *its own* quarter. Used as the
// seed when iterating prior-quarter ranges for the trend.
function computeQuarterFromStart(periodStart) {
  const d = new Date(periodStart);
  if (Number.isNaN(d.getTime())) return null;
  const start = new Date(Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1));
  const end   = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 3, 0));
  const fmt = date => date.toISOString().slice(0, 10);
  const q = Math.floor(start.getUTCMonth() / 3) + 1;
  return {
    periodStart: fmt(start),
    periodEnd: fmt(end),
    quarterLabel: `Q${q} ${start.getUTCFullYear()}`,
    shortLabel: `Q${q} '${String(start.getUTCFullYear()).slice(-2)}`,
  };
}

// Scheduling events count across every schedule-request module on the
// project. Events are occurrence INSTANCES (/v1/<slug>/occurrences — one
// row per event), not the recurring definitions at /v1/<slug>/requests.
// The occurrences list filters on event date via the generic fromDate/
// toDate params, so one limit=1 request per module with the
// FMX-Total-Count response header gives the exact in-period event count.
async function fetchSchedulingSummary({ projectId, scheduleRequestModules, periodStart, periodEnd }) {
  if (!scheduleRequestModules || scheduleRequestModules.length === 0) {
    return { eventsInPeriod: 0 };
  }
  let total = 0;
  let counted = 0;
  for (const m of scheduleRequestModules) {
    try {
      const url = `/v1/${m.slug}/occurrences?fromDate=${encodeURIComponent(periodStart)}`
        + `&toDate=${encodeURIComponent(periodEnd)}&limit=1&fields=id`;
      const res = await fmxFetch({ projectId, endpoint: url, method: 'GET' });
      if (!res.ok) continue;
      const headerTotal = parseInt(res.headers.get('FMX-Total-Count'), 10);
      await res.json().catch(() => null); // drain the 0/1-row body
      if (Number.isFinite(headerTotal)) {
        total += headerTotal;
        counted += 1;
      }
    } catch { /* one module's failure shouldn't kill the count */ }
  }
  // If no module produced a count, report blank rather than a fake 0.
  if (counted === 0) return { eventsInPeriod: null, error: 'No schedule occurrence counts returned' };
  return { eventsInPeriod: total };
}

// Invoicing totals across /v1/invoices for the period. Sums grandTotal
// over invoices whose createdTimestampUtc falls in the quarter. Filters
// server-side with createdFromDate/createdToDate; the client-side date
// loop below stays as the safety net.
async function fetchInvoicingSummary({ projectId, periodStart, periodEnd, warn = null }) {
  const startMs = Date.parse(periodStart);
  const endMs   = Date.parse(periodEnd) + 24 * 3600 * 1000 - 1;
  const createdFrom = encodeURIComponent(new Date(startMs).toISOString());
  const createdTo   = encodeURIComponent(new Date(endMs).toISOString());
  // Only three fields are consumed; without fields= each page carries full
  // invoice payloads. Note the total intentionally includes waived/void
  // invoices (status is pulled for a future exclusion toggle if FMXers
  // want "collected" rather than "invoiced").
  const fields = encodeURIComponent('id,status,grandTotal,createdTimestampUtc');
  const all = [];
  let headerTotal = null;
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = `/v1/invoices?offset=${offset}&limit=${PAGE_LIMIT}&createdFromDate=${createdFrom}&createdToDate=${createdTo}&fields=${fields}`;
    const res = await fmxFetch({ projectId, endpoint: url, method: 'GET' });
    if (!res.ok) return { totalDollars: 0, invoiceCount: 0, error: `Invoices returned ${res.status}` };
    if (page === 0) {
      const t = parseInt(res.headers.get('FMX-Total-Count'), 10);
      if (Number.isFinite(t)) headerTotal = t;
    }
    const body = await res.json();
    const items = Array.isArray(body) ? body : (body.items || body.data || []);
    if (items.length === 0) break;
    all.push(...items);
    if (items.length < PAGE_LIMIT) break;
    offset += PAGE_LIMIT;
  }
  if (warn && headerTotal != null && all.length < headerTotal) {
    warn(`Invoice pull truncated: got ${all.length} of ${headerTotal} matching invoices`);
  }
  let totalDollars = 0;
  let invoiceCount = 0;
  for (const inv of all) {
    const t = Date.parse(inv?.createdTimestampUtc);
    if (Number.isNaN(t) || t < startMs || t > endMs) continue;
    const amt = Number(inv?.grandTotal);
    if (Number.isFinite(amt)) totalDollars += amt;
    invoiceCount += 1;
  }
  return { totalDollars: round1(totalDollars), invoiceCount };
}

// Lighter prior-quarter fetch: pool WRs across modules, compute the
// timing series. No PMs, no per-module breakdown.
async function fetchPriorQuarterSummary({
  projectId, workRequestModules, periodStart, periodEnd, quarterLabel,
}) {
  const startMs = Date.parse(periodStart);
  const endMs   = Date.parse(periodEnd) + DAY_MS - 1;

  const wrPulls = workRequestModules.map(wr =>
    paginateCreatedSince({
      projectId, endpoint: workRequestEndpoint(wr.slug), startMs, endMs,
    }).then(all => filterInPeriod(all, startMs, endMs)).catch(() => [])
  );
  const inPeriodPerModule = await Promise.all(wrPulls);
  const allInPeriod = inPeriodPerModule.flat();

  // WR_FIELDS already includes `actions`, so this pull carries the same
  // inline action arrays the current-quarter pull uses. Feed them
  // through so PRIOR_RESPONSE_DAYS measures true first-response like
  // the current quarter — previously this passed null and every prior
  // "response" silently fell back to the resolution timestamp, making
  // the slide-9 quarter-over-quarter comparison apples-to-oranges. No
  // bulk-id fallback here (cost guard): rows without inline actions
  // still degrade to the resolution anchor.
  const actionsByWrId = new Map();
  for (const wo of allInPeriod) {
    if (wo?.id != null && Array.isArray(wo.actions)) actionsByWrId.set(wo.id, wo.actions);
  }
  const response = computeResponseTime(allInPeriod, actionsByWrId);

  // Prior-quarter adoption is deliberately NOT computed. The API
  // exposes only lastLoginTimeUtc — a single, overwritten snapshot
  // with no login history behind it — so "active during the prior
  // quarter" is unknowable after the fact: anyone who logged in any
  // time since would have counted too. The deck's adoption trend line
  // renders an em-dash instead of a fabricated comparison.
  return {
    quarterLabel,
    periodStart,
    periodEnd,
    response,
    adoption: null,
    woCount: allInPeriod.length,
  };
}

// Fetches the project's equipment list and returns a Map<id,
// {id, name, buildingID}>. Used by slide 14 (Asset Health top 5) to
// render equipment name + the building it lives in. EquipmentItemReadModel
// has no `name` property — the asset identifier is `tag`, with
// `hierarchicalName` as the fuller display string ("Parent > Child").
// Capped at 5000 items in one page; FMX-Total-Count flags larger tenants.
async function fetchEquipmentLookup(projectId, warn = null) {
  const map = new Map();
  const res = await fmxFetch({
    projectId, endpoint: '/v1/equipment?limit=5000&fields=id,tag,hierarchicalName,buildingID',
    method: 'GET',
  });
  if (!res.ok) return map;
  const body = await res.json().catch(() => null);
  const items = Array.isArray(body) ? body : (body?.items || body?.data || []);
  const headerTotal = parseInt(res.headers.get('FMX-Total-Count'), 10);
  if (warn && Number.isFinite(headerTotal) && items.length < headerTotal) {
    warn(`Equipment lookup truncated: got ${items.length} of ${headerTotal} items — some Asset Health names may fall back to IDs`);
  }
  for (const e of items) {
    if (e?.id != null) {
      map.set(e.id, {
        id: e.id,
        name: e.hierarchicalName || e.tag || `Equipment ${e.id}`,
        buildingID: e.buildingID ?? null,
      });
    }
  }
  return map;
}

// Fetches the project's building list and returns a Map<id, {id, name}>.
// Pulled once per QBR run; the count was already pre-fetched in the UI
// for the 'Basics' card, but the deck needs actual names for slides 8
// and 11. Caps at 1000 buildings — single page covers every customer
// we've seen.
async function fetchBuildingsLookup(projectId, warn = null) {
  const map = new Map();
  const res = await fmxFetch({
    projectId, endpoint: '/v1/buildings?limit=1000&fields=id,name',
    method: 'GET',
  });
  if (!res.ok) return map;
  const body = await res.json().catch(() => null);
  const items = Array.isArray(body) ? body : (body?.items || body?.data || []);
  const headerTotal = parseInt(res.headers.get('FMX-Total-Count'), 10);
  if (warn && Number.isFinite(headerTotal) && items.length < headerTotal) {
    warn(`Building lookup truncated: got ${items.length} of ${headerTotal} buildings`);
  }
  for (const b of items) {
    if (b?.id != null) map.set(b.id, { id: b.id, name: b.name || `Building ${b.id}` });
  }
  return map;
}

// Roll up per-module results into a single "Overall" view. Math:
//   completion_rate  → sum completed / sum total
//   backlog_aging    → sum overdue30dCount / sum openCount; max oldestDays
//   response_time    → resolutionDays = weighted avg of module medians
//                      (weighted by completed-WO count). Approximate but
//                      good enough; we don't keep raw per-WO times around.
//   planned_vs_reactive → sum planned / sum reactive
//   pm_compliance    → sum totalClosed/total, sum closedOnTime/totalWithDue
//   adoption         → same global value (already tenant-level)
function aggregateModules(byModule, { adoption, adoptionPower }) {
  const modules = Object.values(byModule);
  if (modules.length === 0) {
    return { rawCounts: {}, metrics: emptyMetrics() };
  }

  const sum = (k) => modules.reduce((a, m) => a + (m.rawCounts?.[k] || 0), 0);

  // completion_rate
  let comp = null;
  const cTotal = modules.reduce((a, m) => a + (m.metrics.completion_rate?.total || 0), 0);
  const cDone  = modules.reduce((a, m) => a + (m.metrics.completion_rate?.completed || 0), 0);
  if (cTotal > 0) comp = { completed: cDone, total: cTotal };

  // backlog_aging
  const openTotal = modules.reduce((a, m) => a + (m.metrics.backlog_aging?.openCount || 0), 0);
  const overdueTotal = modules.reduce((a, m) => a + (m.metrics.backlog_aging?.overdue30dCount || 0), 0);
  const oldestDaysAll = modules
    .map(m => m.metrics.backlog_aging?.oldestDays)
    .filter(d => d != null);
  let backlog = null;
  if (openTotal > 0 || modules.some(m => m.metrics.backlog_aging)) {
    backlog = {
      overdue30dPct: openTotal > 0 ? round1((overdueTotal / openTotal) * 100) : 0,
      oldestDays:    oldestDaysAll.length ? Math.max(...oldestDaysAll) : 0,
      openCount:        openTotal,
      overdue30dCount:  overdueTotal,
    };
  }

  // response_time — weighted by per-module sample counts. Each of the
  // three series (response / assignment / resolution) is rolled up
  // independently with its own N because a single WR can contribute to
  // some series and not others (e.g. resolved without a comment ⇒ has
  // a resolution delta but no separate response delta).
  let response = null;
  const respModules = modules.map(m => m.metrics.response_time).filter(Boolean);
  if (respModules.length > 0) {
    const rollSeries = (prefix) => {
      const days = respModules.map(r => r[`${prefix}Days`]);
      const mean = respModules.map(r => r[`${prefix}MeanDays`]);
      const mode = respModules.map(r => r[`${prefix}ModeDays`]);
      const n    = respModules.map(r => r[`${prefix}N`] || 0);
      const total = n.reduce((a, b) => a + b, 0);
      return {
        [`${prefix}Days`]:     weightedAvg(days, n),
        [`${prefix}MeanDays`]: weightedAvg(mean, n),
        [`${prefix}ModeDays`]: modeOfModes(mode, n),
        [`${prefix}N`]:        total,
      };
    };
    response = {
      ...rollSeries('response'),
      ...rollSeries('assignment'),
      ...rollSeries('resolution'),
    };
  }

  // planned_vs_reactive — Overall-only. Pools every completed WR and
  // every completed PM across the whole pull, regardless of whether
  // they share a slug. The deck's Overall section is the only place
  // this metric is shown; per-scope sections omit it (see
  // scopeMetrics() in QbrDataStep).
  const totalCompletedWrs = modules.reduce(
    (a, m) => a + (m.metrics?.completion_rate?.completed || 0),
    0
  );
  const totalCompletedPms = modules.reduce(
    (a, m) => a + (m.metrics?.pm_compliance?.totalClosed || 0),
    0
  );
  const pvr = (totalCompletedWrs > 0 || totalCompletedPms > 0)
    ? { planned: totalCompletedPms, reactive: totalCompletedWrs }
    : null;

  // pm_compliance
  const pmScheduled = modules.reduce((a, m) => a + (m.metrics.pm_compliance?.totalScheduled || 0), 0);
  const pmClosed    = modules.reduce((a, m) => a + (m.metrics.pm_compliance?.totalClosed    || 0), 0);
  const pmWithDue   = modules.reduce((a, m) => a + (m.metrics.pm_compliance?.totalWithDue   || 0), 0);
  // Reconstruct on-time count from each module's onTimePct × totalWithDue.
  const pmOnTime = modules.reduce((a, m) => {
    const c = m.metrics.pm_compliance;
    if (!c || c.onTimePct == null || !c.totalWithDue) return a;
    return a + Math.round((c.onTimePct / 100) * c.totalWithDue);
  }, 0);
  const target = modules.find(m => m.metrics.pm_compliance?.targetPct != null)
    ?.metrics.pm_compliance.targetPct ?? null;
  const pmComp = pmScheduled > 0 ? {
    onTimePct:  pmWithDue > 0 ? round1((pmOnTime / pmWithDue) * 100) : null,
    anyTimePct: round1((pmClosed / pmScheduled) * 100),
    targetPct:  target,
    totalScheduled: pmScheduled,
    totalClosed:    pmClosed,
    totalWithDue:   pmWithDue,
  } : null;

  // Slide-specific aggregations rolled up across modules. Most are
  // sum-/concat-/max-style rollups; topRequestors/topBuildings re-rank
  // because a building or user may appear in multiple modules.
  const statusBreakdown = rollStatusBreakdown(modules);
  const topBuildings    = rollTopBy(modules, 'topBuildings', 5);
  const topRequestors   = rollTopBy(modules, 'topRequestors', 5);
  const ageBuckets      = rollAgeBuckets(modules);
  const topOverduePms   = rollTopOverduePms(modules, 3);
  const topEquipment    = rollTopEquipment(modules, 5);

  return {
    rawCounts: {
      pulled:    sum('pulled'),
      inPeriod:  sum('inPeriod'),
      pmPulled:  sum('pmPulled'),
      pmInPeriod: sum('pmInPeriod'),
    },
    statusBreakdown,
    topBuildings,
    topRequestors,
    ageBuckets,
    topOverduePms,
    topEquipment,
    metrics: {
      completion_rate:     comp,
      response_time:       response,
      backlog_aging:       backlog,
      planned_vs_reactive: pvr,
      pm_compliance:       pmComp,
      adoption,
      adoption_power:      adoptionPower,
    },
  };
}

// Sum status counts across modules; recompute pct against the new total.
function rollStatusBreakdown(modules) {
  const all = modules.map(m => m.statusBreakdown).filter(Boolean);
  if (all.length === 0) return null;
  const out = { completedCount: 0, inProgressCount: 0, pendingCount: 0, overdueCount: 0, total: 0 };
  for (const s of all) {
    out.completedCount  += s.completedCount  || 0;
    out.inProgressCount += s.inProgressCount || 0;
    out.pendingCount    += s.pendingCount    || 0;
    out.overdueCount    += s.overdueCount    || 0;
    out.total           += s.total           || 0;
  }
  const pct = c => out.total > 0 ? round1((c / out.total) * 100) : 0;
  out.completedPct  = pct(out.completedCount);
  out.inProgressPct = pct(out.inProgressCount);
  out.pendingPct    = pct(out.pendingCount);
  out.overduePct    = pct(out.overdueCount);
  return out;
}

// Re-rank top-N entries pooled across all modules. Each entry is
// { id, name?, count }; same id appearing in multiple modules sums.
function rollTopBy(modules, key, n) {
  const counts = new Map();
  const names  = new Map();
  const locations = new Map();
  for (const m of modules) {
    for (const entry of (m[key] || [])) {
      const id = entry.id ?? entry.userID ?? entry.buildingID;
      if (id == null) continue;
      counts.set(id, (counts.get(id) || 0) + (entry.count || 0));
      if (entry.name && !names.has(id)) names.set(id, entry.name);
      if (entry.location && !locations.has(id)) locations.set(id, entry.location);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([id, count]) => {
      const out = { id, count };
      if (names.has(id)) out.name = names.get(id);
      if (locations.has(id)) out.location = locations.get(id);
      // Carry through alt-shape fields for the requestor view.
      if (key === 'topRequestors') out.userID = id;
      return out;
    });
}

function rollAgeBuckets(modules) {
  const out = { d0to7: 0, d8to14: 0, d15to30: 0, dOver30: 0 };
  let any = false;
  for (const m of modules) {
    if (!m.ageBuckets) continue;
    any = true;
    out.d0to7   += m.ageBuckets.d0to7   || 0;
    out.d8to14  += m.ageBuckets.d8to14  || 0;
    out.d15to30 += m.ageBuckets.d15to30 || 0;
    out.dOver30 += m.ageBuckets.dOver30 || 0;
  }
  return any ? out : null;
}

function rollTopEquipment(modules, n) {
  const byId = new Map();
  for (const m of modules) {
    for (const e of (m.topEquipment || [])) {
      const prev = byId.get(e.id) || { id: e.id, name: e.name, locationName: e.locationName, count: 0 };
      prev.count += e.count;
      byId.set(e.id, prev);
    }
  }
  return [...byId.values()].sort((a, b) => b.count - a.count).slice(0, n);
}

function rollTopOverduePms(modules, n) {
  const byTask = new Map();
  for (const m of modules) {
    for (const t of (m.topOverduePms || [])) {
      const k = t.taskName;
      const prev = byTask.get(k) || { taskName: k, count: 0, buildingID: t.buildingID, buildingName: t.buildingName };
      prev.count += t.count;
      byTask.set(k, prev);
    }
  }
  return [...byTask.values()].sort((a, b) => b.count - a.count).slice(0, n);
}

function weightedAvg(vals, weights) {
  let sum = 0, w = 0;
  for (let i = 0; i < vals.length; i++) {
    if (vals[i] == null) continue;
    sum += vals[i] * weights[i];
    w += weights[i];
  }
  return w > 0 ? round1(sum / w) : null;
}

// Approximate roll-up of per-module mode days: each module contributes
// its mode day weighted by its N. Pick the day-bucket with the largest
// total weight. Loses fidelity (we don't have raw deltas to re-bucket)
// but matches the user's intent of a single "most common turnaround" at
// the Overall level.
function modeOfModes(modes, weights) {
  const totals = new Map();
  for (let i = 0; i < modes.length; i++) {
    if (modes[i] == null) continue;
    const w = weights[i] || 0;
    if (w === 0) continue;
    totals.set(modes[i], (totals.get(modes[i]) || 0) + w);
  }
  let best = null, bestWeight = -1;
  for (const [day, w] of totals) {
    if (w > bestWeight || (w === bestWeight && day < best)) { best = day; bestWeight = w; }
  }
  return best;
}

function emptyMetrics() {
  return {
    completion_rate: null, response_time: null, backlog_aging: null,
    planned_vs_reactive: null, pm_compliance: null, adoption: null,
  };
}

// Per-scope pull. A scope is keyed by slug and can carry a work-request
// module, a work-task module, or both. Whichever side exists drives which
// metrics get computed:
//
//   hasWR  → completion_rate, response_time, backlog_aging
//   hasWT  → pm_compliance
//   both   → planned_vs_reactive (also)
//
// Adoption is global and runs once at the wrapper, not here.
async function pullOneScope({ projectId, scope, pmTargetPct, periodStart, periodEnd, buildingLookup, equipmentLookup, warn = null }) {
  const startMs = Date.parse(periodStart);
  const endMs   = Date.parse(periodEnd) + 24 * 3600 * 1000 - 1;

  let woAll = [];
  let woInPeriod = [];
  let woOpen = null;
  let wrEndpointUsed = null;
  if (scope.wrModule) {
    wrEndpointUsed = workRequestEndpoint(scope.wrModule.slug);
    woAll = await paginateCreatedSince({
      projectId, endpoint: wrEndpointUsed, startMs, endMs, warn,
    });
    woInPeriod = filterInPeriod(woAll, startMs, endMs);
    // Backlog/aging needs every currently-open WR, including ones
    // created before the quarter — the in-period pull can't see those.
    // On failure fall back to the in-period set (degraded, warned)
    // rather than killing the whole scope.
    woOpen = await fetchOpenWorkRequests({
      projectId, endpoint: wrEndpointUsed, warn,
    }).catch(() => null);
    if (woOpen == null && warn) {
      warn(`Open-backlog pull failed for ${scope.slug} — backlog/aging computed from in-quarter rows only`);
    }
  }

  let pmAll = [];
  let pmInPeriod = [];
  let pmOverdue = null;
  let pmSampleKeys = [];
  let pmEndpointUsed = null;
  if (scope.wtModule) {
    pmEndpointUsed = workTaskOccurrencesEndpoint(scope.wtModule.slug);
    // Windowed on the scheduled `date` via dueFromDate/dueToDate — the
    // same anchor pmOccurrenceAnchorMs uses — so the pull covers exactly
    // the quarter instead of the tenant's entire occurrence history
    // (or the future occurrences FMX pre-generates).
    pmAll = await paginateOccurrences({
      projectId, endpoint: pmEndpointUsed, fromMs: startMs, toMs: endMs, warn,
    });
    pmInPeriod = pmAll.filter(o => {
      const t = pmOccurrenceAnchorMs(o);
      return t != null && t >= startMs && t <= endMs;
    });
    // Overdue PMs (slide 11) must include occurrences scheduled BEFORE the
    // window — long-overdue ones matter most — so pull them separately,
    // filtered server-side. Falls back to the windowed set on failure.
    pmOverdue = await fetchOverdueOccurrences({ projectId, endpoint: pmEndpointUsed })
      .catch(() => null);
    const pmSample = pmInPeriod[0] || pmAll[0];
    pmSampleKeys = pmSample ? Object.keys(pmSample).sort() : [];
  }

  const hasWR = !!scope.wrModule;
  const hasWT = !!scope.wtModule;

  // Fetch first-response actions for in-period WRs. The user wants
  // first-response = first 'response' (comment) action, falling back to
  // the resolution timestamp. If the list endpoint already returned an
  // `actions` array per WR we use it; otherwise we hit the single-
  // resource GET in parallel batches.
  let actionsByWrId = new Map();
  if (hasWR && woInPeriod.length > 0) {
    const firstWithActions = woInPeriod.find(wo => Array.isArray(wo.actions));
    if (firstWithActions) {
      for (const wo of woInPeriod) {
        if (Array.isArray(wo.actions)) actionsByWrId.set(wo.id, wo.actions);
      }
    } else {
      actionsByWrId = await fetchWrActions({
        projectId,
        moduleSlug: scope.wrModule.slug,
        wrIds: woInPeriod.map(wo => wo.id).filter(id => id != null),
      });
    }
  }

  // Backlog and age buckets read the open-statuses pull (all open WRs,
  // any age) and are a snapshot "as of now" — statuses always were
  // current-state, so anchoring ages to now keeps the two consistent.
  // The in-quarter set is the degraded fallback when the open pull
  // failed (warned above).
  const backlogSource = woOpen ?? woAll;
  const nowMs = Date.now();

  const completion = hasWR ? computeCompletion(woInPeriod) : null;
  const response   = hasWR ? computeResponseTime(woInPeriod, actionsByWrId) : null;
  const backlog    = hasWR ? computeBacklog(backlogSource, nowMs) : null;
  // planned_vs_reactive is intentionally NOT computed per-scope.
  // It's an Overall-only metric (see aggregateModules) — pooling
  // every completed WR + every completed PM across the whole pull.
  const pvr = null;
  const pmComp     = hasWT ? computePmCompliance(pmInPeriod, pmTargetPct) : null;

  // Per-slide aggregations on top of the core metrics. These don't go
  // into the rubric — they fill template tokens for the storytelling
  // slides (status breakdown table, top buildings, age buckets, top
  // requestors, top overdue PMs). All derive from data we already pulled.
  const statusBreakdown = hasWR ? computeStatusBreakdown(woInPeriod, endMs) : null;
  const topBuildings    = hasWR ? topByGroup(woInPeriod, wo => wo.buildingID, 5, buildingLookup) : null;
  const topRequestors   = hasWR ? topByGroup(woInPeriod, wo => wo.requestingUserID, 5).map(r => ({ userID: r.id, count: r.count })) : null;
  const ageBuckets      = hasWR ? computeAgeBuckets(backlogSource, nowMs) : null;
  const topOverduePms   = hasWT ? computeTopOverduePms(pmOverdue ?? pmAll, buildingLookup, 3) : null;
  const topEquipment    = hasWR ? computeTopEquipment(woInPeriod, equipmentLookup, buildingLookup, 5) : null;

  const woSample = woInPeriod[0] || woAll[0];
  return {
    moduleSlug:  scope.slug,
    moduleLabel: scope.label,
    hasWR, hasWT,
    rawCounts: {
      pulled: woAll.length,
      inPeriod: woInPeriod.length,
      openPulled: woOpen ? woOpen.length : null,
      pmPulled: pmAll.length,
      pmInPeriod: pmInPeriod.length,
    },
    fieldsSeen:   hasWR ? inspectFields(woSample) : null,
    sampleKeys:   woSample ? Object.keys(woSample).sort() : [],
    pmSampleKeys,
    wrEndpointUsed,
    pmEndpointUsed,
    dateRange:    hasWR && woAll.length > 0 ? computeDateRange(woAll) : null,
    pmDateRange:  pmAll.length > 0 ? computePmDateRange(pmAll) : null,
    // Per-slide aggregations — read by the deck builder for tokens
    // beyond the headline metrics. Kept outside the `metrics` block so
    // the grading + rubric pipeline ignores them.
    statusBreakdown,
    topBuildings,
    topRequestors,
    ageBuckets,
    topOverduePms,
    topEquipment,
    metrics: {
      completion_rate:     completion,
      response_time:       response,
      backlog_aging:       backlog,
      planned_vs_reactive: pvr,
      pm_compliance:       pmComp,
    },
  };
}

function filterInPeriod(rows, startMs, endMs) {
  return rows.filter(r => {
    const t = woCreatedMs(r);
    return t != null && t >= startMs && t <= endMs;
  });
}

// Planned vs Reactive ratio. Per the rubric, this measures **completed
// work** — PMs (planned) that were successfully Completed vs WRs
// (reactive) that were Finalized. Counting in-period created rows
// (the old behaviour) inflated the denominator with not-yet-finished
// work and bled across customers with long-running tickets.
export function computePlannedVsReactive(pmInPeriod, woInPeriod, hasPmModule) {
  if (!hasPmModule) return null;
  const planned  = pmInPeriod.filter(pmIsCompleted).length;
  const reactive = woInPeriod.filter(woIsCompleted).length;
  if (planned === 0 && reactive === 0) return null;
  return { planned, reactive };
}

// PM occurrence completion. FMX's own "completed on time" filter gates
// on the Completed STATE first, then the execution date
// (WorkTaskOccurrenceFilterPredicates.cs:208-212) — an occurrence can
// be 'Partially completed' and still carry an execution timestamp, so
// the timestamp alone is not proof of completion. The timestamp only
// decides for rows whose payload has no status at all (defensive for
// field-trimmed responses).
export function pmIsCompleted(pm) {
  const k = statusKey(statusLabel(pm));
  if (k != null) return COMPLETED_STATUSES.has(k);
  return woCompletedMs(pm) != null;
}

// Two complementary numbers per the grading rubric: an "on-time"
// completion rate (closed on/before dueDate) and an "any-time" rate
// (closed at all). The grade is computed against on-time vs target;
// any-time is shown for context. Both flow back into the form so the
// FMXer can override either.
export function computePmCompliance(pmInPeriod, targetPct) {
  if (pmInPeriod.length === 0) return null;
  let closed = 0;
  let closedOnTime = 0;
  let totalWithDue = 0;
  for (const pm of pmInPeriod) {
    const closedMs = woCompletedMs(pm);
    // PM "completion" = status 'Completed' (success). Deleted/Skipped/
    // Retired are terminal but unsuccessful, and 'Partially completed'
    // is neither — see pmIsCompleted.
    const isCompleted = pmIsCompleted(pm);
    if (isCompleted) closed += 1;
    const due = woDueMs(pm);
    if (due != null) {
      totalWithDue += 1;
      // On-time is day-granular, matching FMX
      // (executionTimeUtc.Value.Date <= date): the occurrence `date`
      // parses to midnight, so execution any time ON the scheduled day
      // — i.e. before the next midnight — still counts as on-time. The
      // old full-timestamp compare (closedMs <= due) marked every
      // same-day execution late.
      if (isCompleted && closedMs != null && closedMs < due + DAY_MS) closedOnTime += 1;
    }
  }
  const total = pmInPeriod.length;
  const anyTimePct = round1((closed / total) * 100);
  const onTimePct  = totalWithDue > 0 ? round1((closedOnTime / totalWithDue) * 100) : null;
  return {
    onTimePct,
    anyTimePct,
    targetPct: targetPct != null ? Number(targetPct) : null,
    totalScheduled: total,
    totalClosed: closed,
    totalWithDue,
  };
}

function computeDateRange(all) {
  return rangeFromFn(all, woCreatedMs);
}

function computePmDateRange(all) {
  // Also surface the raw value of the first PM's anchor field so we can
  // see whether the date string actually parses.
  const out = rangeFromFn(all, pmOccurrenceAnchorMs);
  const sample = all[0];
  if (sample) {
    for (const k of PM_OCCURRENCE_ANCHOR_KEYS) {
      if (sample[k] != null) {
        out.anchorField = k;
        out.sampleAnchorValue = String(sample[k]);
        break;
      }
    }
  }
  return out;
}

function rangeFromFn(all, fn) {
  let min = null, max = null, parsed = 0;
  for (const r of all) {
    const t = fn(r);
    if (t == null) continue;
    parsed += 1;
    if (min == null || t < min) min = t;
    if (max == null || t > max) max = t;
  }
  if (min == null) return { parsed: 0 };
  const fmt = ms => new Date(ms).toISOString().slice(0, 10);
  return { earliest: fmt(min), latest: fmt(max), parsed };
}

// PM occurrences don't have a clear "created" timestamp — their position
// in time is the scheduled date. Try the likely candidates and use the
// first one that parses; falls through to createdTimeUtc for tenants
// that expose it on occurrences.
// Confirmed against a real tenant: PM occurrences have a top-level
// `date` field representing the scheduled day. Other names retained as
// safe fallbacks for tenants whose schema differs.
const PM_OCCURRENCE_ANCHOR_KEYS = [
  'date',
  'scheduledTimeUtc', 'scheduledOn', 'scheduledAt', 'scheduledFor',
  'dueDate', 'dueTimeUtc', 'dueOn',
  'createdTimeUtc', 'createdOn',
];

function pmOccurrenceAnchorMs(o) {
  return woTimestampMs(o, PM_OCCURRENCE_ANCHOR_KEYS);
}

// Fetches the `actions` array for each in-period WR. This is a rare
// fallback: the list endpoint returns actions inline when `actions` is in
// the explicit fields= list (it's a default-excluded field, but explicit
// listing overrides that), so this only runs when no in-period WR carried
// a non-empty actions array. Uses the bulk-by-IDs route (2–100 comma-
// separated ids per request) instead of one GET per WR — worst case ~20
// requests instead of ~2,000 against the 1000-reads/10min rate limit.
const WR_BULK_CHUNK = 100;
async function fetchWrActions({ projectId, moduleSlug, wrIds }) {
  const out = new Map();
  for (let i = 0; i < wrIds.length; i += WR_BULK_CHUNK) {
    const chunk = wrIds.slice(i, i + WR_BULK_CHUNK);
    // The {ids} route constraint requires ≥2 ids; a lone id uses the
    // single-record route (same response shape, just not an array).
    const idsPath = chunk.join(',');
    try {
      const res = await fmxFetch({
        projectId,
        endpoint: `/v1/${moduleSlug}-requests/${idsPath}?fields=${encodeURIComponent('id,actions')}`,
        method: 'GET',
      });
      if (!res.ok) continue;
      const body = await res.json();
      const records = Array.isArray(body) ? body : (body ? [body] : []);
      for (const r of records) {
        if (r?.id != null) out.set(r.id, Array.isArray(r.actions) ? r.actions : []);
      }
    } catch { /* chunk failure → those WRs fall back to resolution timestamps */ }
  }
  return out;
}

// PM occurrence pull, windowed on the scheduled `date` column via
// dueFromDate/dueToDate — the documented filter pair for occurrence
// lists ("for work task occurrences, the due date is the scheduled
// occurrence date" per WorkTaskOccurrenceDescriptions; createdFromDate
// filters the same column but is undocumented on this endpoint). The
// upper bound keeps future-scheduled occurrences — which FMX generates
// ahead of time — out of the pull entirely.
async function paginateOccurrences({ projectId, endpoint, fromMs = null, toMs = null, fields = OCC_FIELDS, warn = null }) {
  const out = [];
  const seen = new Set();
  const fromParam = fromMs != null
    ? `&dueFromDate=${encodeURIComponent(new Date(fromMs).toISOString())}`
    : '';
  const toParam = toMs != null
    ? `&dueToDate=${encodeURIComponent(new Date(toMs).toISOString())}`
    : '';
  let headerTotal = null;
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = `${endpoint}?offset=${offset}&limit=${PAGE_LIMIT}${fromParam}${toParam}&fields=${encodeURIComponent(fields)}`;
    const res = await fmxFetch({ projectId, endpoint: url, method: 'GET' });
    if (!res.ok) throw new Error(await fmxErrorText(res, endpoint));
    if (page === 0) {
      const t = parseInt(res.headers.get('FMX-Total-Count'), 10);
      if (Number.isFinite(t)) headerTotal = t;
    }
    const body = await res.json();
    const items = Array.isArray(body) ? body : (body.items || body.data || body.results || []);
    if (items.length === 0) break;
    for (const r of items) {
      if (r?.id != null) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
      }
      out.push(r);
    }
    if (items.length < PAGE_LIMIT) break;
    offset += PAGE_LIMIT;
  }
  if (warn && headerTotal != null && out.length < headerTotal) {
    warn(`Occurrence pull truncated at ${endpoint}: got ${out.length} of ${headerTotal} rows`);
  }
  return out;
}

// Fields for the open-backlog pull — just enough for computeBacklog and
// computeAgeBuckets. `status` rides along so woIsClosed can re-check
// rows the server already filtered to open.
const OPEN_WR_FIELDS = ['id', 'createdTimeUtc', 'dueDate', 'status'].join(',');

// Every currently-open WR in the module, regardless of creation date —
// server-filtered via statuses=open (FilterStatus.Open → the four
// IsOpen states: Pending details / approval / assignment / resolution,
// per WorkRequestStates.cs). That set matches woIsClosed's view of
// "open": resolved-pending-finalization rows are excluded on both
// sides. Backlog/aging must come from this pull — deriving it from the
// created-since-periodStart pull (the old behaviour) silently ignored
// open WRs created before the quarter and capped "oldest open" at the
// quarter length.
async function fetchOpenWorkRequests({ projectId, endpoint, warn = null }) {
  const out = [];
  const seen = new Set();
  let headerTotal = null;
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = `${endpoint}?offset=${offset}&limit=${PAGE_LIMIT}&statuses=open&fields=${encodeURIComponent(OPEN_WR_FIELDS)}`;
    const res = await fmxFetch({ projectId, endpoint: url, method: 'GET' });
    if (!res.ok) throw new Error(await fmxErrorText(res, `${endpoint} open-backlog pull`));
    if (page === 0) {
      const t = parseInt(res.headers.get('FMX-Total-Count'), 10);
      if (Number.isFinite(t)) headerTotal = t;
    }
    const body = await res.json();
    const items = Array.isArray(body) ? body : (body.items || body.data || body.results || []);
    if (items.length === 0) break;
    for (const r of items) {
      if (r?.id != null) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
      }
      out.push(r);
    }
    if (items.length < PAGE_LIMIT) break;
    offset += PAGE_LIMIT;
  }
  if (warn && headerTotal != null && out.length < headerTotal) {
    warn(`Open-backlog pull truncated at ${endpoint}: got ${out.length} of ${headerTotal} open rows`);
  }
  return out;
}

// Overdue PM occurrences regardless of scheduled date — server-filtered
// via statuses=overdue so the long-overdue ones (scheduled before the
// current window) still surface on slide 11. Small result set by nature.
async function fetchOverdueOccurrences({ projectId, endpoint }) {
  const out = [];
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = `${endpoint}?offset=${offset}&limit=${PAGE_LIMIT}&statuses=overdue&fields=${encodeURIComponent(OCC_FIELDS)}`;
    const res = await fmxFetch({ projectId, endpoint: url, method: 'GET' });
    if (!res.ok) throw new Error(await fmxErrorText(res, `${endpoint} overdue pull`));
    const body = await res.json();
    const items = Array.isArray(body) ? body : (body.items || body.data || body.results || []);
    if (items.length === 0) break;
    out.push(...items);
    if (items.length < PAGE_LIMIT) break;
    offset += PAGE_LIMIT;
  }
  return out;
}

// Pull every record created in [startMs, endMs] using the API's
// server-side createdFromDate/createdToDate filters. (The API has no
// sortBy/sortDirection params — its sort keys are org-specific `sort=`
// values — so the previous newest-first early-termination walk silently
// ran against default order and could stop after one page. Server-side
// filtering replaces it.) The upper bound matters for pagination
// stability: the default WR order is ID DESCENDING, so without it a row
// created mid-pull matches the filter, lands at offset 0, and shifts
// every later page. Rows are deduped by id as an extra guard. If a
// tenant/endpoint ignores the date params this degrades to a full walk
// capped at MAX_PAGES; callers still trim with filterInPeriod.
async function paginateCreatedSince({ projectId, endpoint, startMs, endMs = null, fields = WR_FIELDS, warn = null }) {
  const out = [];
  const seen = new Set();
  const createdFrom = encodeURIComponent(new Date(startMs).toISOString());
  const createdTo = endMs != null
    ? `&createdToDate=${encodeURIComponent(new Date(endMs).toISOString())}`
    : '';
  let headerTotal = null;
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = `${endpoint}?offset=${offset}&limit=${PAGE_LIMIT}&createdFromDate=${createdFrom}${createdTo}&fields=${encodeURIComponent(fields)}`;
    const res = await fmxFetch({ projectId, endpoint: url, method: 'GET' });
    if (!res.ok) throw new Error(await fmxErrorText(res, endpoint));
    if (page === 0) {
      const t = parseInt(res.headers.get('FMX-Total-Count'), 10);
      if (Number.isFinite(t)) headerTotal = t;
    }
    const body = await res.json();
    const items = Array.isArray(body) ? body : (body.items || body.data || body.results || []);
    if (items.length === 0) break;
    for (const r of items) {
      if (r?.id != null) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
      }
      out.push(r);
    }
    if (items.length < PAGE_LIMIT) break;
    offset += PAGE_LIMIT;
  }
  if (warn && headerTotal != null && out.length < headerTotal) {
    warn(`Pull truncated at ${endpoint}: got ${out.length} of ${headerTotal} rows`);
  }
  return out;
}

// Field-name candidate lists. The first key that's present + parses as a
// date wins. Order matters: the canonical FMX field for each role goes
// first; older / tenant-specific names are fallbacks.
const CREATED_KEYS   = ['createdTimeUtc', 'createdOn', 'createdAt', 'createdTime', 'requestedOn', 'submittedOn'];
// `resolutionTimeUtc` is the canonical WR completion timestamp per OpenAPI
// ("UTC timestamp of the most recent resolution"). `executionTimeUtc` is
// the corresponding field on PM occurrences.
const COMPLETED_KEYS = ['resolutionTimeUtc', 'executionTimeUtc', 'completedTimeUtc', 'completedOn', 'completedAt', 'closedTimeUtc', 'closedOn', 'closedAt', 'resolvedTimeUtc'];
// First-response is now derived from /v1/<slug>-requests/{id}.actions
// (a 'response' action = a user comment) with fall-through to the
// resolution timestamp. No simple top-level field to probe.
// For PM occurrences, the `date` field is both the scheduled date and the
// implicit due date — completion on or before it counts as on-time.
const DUE_KEYS       = ['dueDate', 'dueOn', 'dueAt', 'dueTime', 'date'];

// Fields we want from /v1/<slug>-requests. All names verified against
// WorkRequestReadModel. `actions` is default-excluded by the API but an
// explicit fields= listing overrides the exclusion, so the LIST endpoint
// returns action objects inline — the bulk fallback in fetchWrActions
// only runs when no in-period WR carried a non-empty actions array.
const WR_FIELDS = [
  'id', 'name', 'kind', 'module',
  'createdTimeUtc', 'assignedTimeUtc', 'resolutionTimeUtc', 'declinedTimeUtc',
  'dueDate', 'status', 'workflowStatus',
  'requestTypeID', 'buildingID', 'requestingUserID', 'assignedUserIDs',
  'resolvingUserID', 'priorityLevel', 'actions',
  // For slide 14 Asset Health grouping.
  'equipmentItemIDs',
].join(',');

// Fields for /v1/<slug>/occurrences — verified against
// WorkTaskOccurrenceReadModel. Everything PM compliance, the trend, and
// slide 11 consume; drops the cost/meter/custom-field payload weight.
const OCC_FIELDS = [
  'id', 'taskID', 'taskName', 'status', 'date', 'executionTimeUtc',
  'buildingIDs',
].join(',');

function woTimestampMs(wo, keys) {
  if (!wo) return null;
  for (const k of keys) {
    if (wo[k]) {
      const t = Date.parse(wo[k]);
      if (!Number.isNaN(t)) return t;
    }
  }
  return null;
}

function woCreatedMs(wo)   { return woTimestampMs(wo, CREATED_KEYS); }
function woCompletedMs(wo) { return woTimestampMs(wo, COMPLETED_KEYS); }
function woDueMs(wo)       { return woTimestampMs(wo, DUE_KEYS); }

// A dueDate is a calendar date (parses to midnight). FMX treats a row
// as past due starting the day AFTER its due date (IsPastDue:
// Today > Date), so the boundary is due-midnight + 24h — a WR due
// "today" isn't overdue until tomorrow. The old raw compare
// (due < asOf) flagged rows overdue the moment their due DAY started.
function isPastDueMs(dueMs, asOfMs) {
  return dueMs != null && asOfMs >= dueMs + DAY_MS;
}

// Status vocabularies, verified against the Fmx.ApiCore state classes.
// Explicit sets rather than substring regexes because substring matching
// produced "Email disabled"-style false positives.
//
//   Work-Request `status` (WorkRequestState.GetName): "Pending details",
//   "Pending assignment", "Pending resolution", "Pending finalization",
//   "Finalized" (success), "Declined", "Deleted", plus dynamic approval
//   strings ("Pending my approval", "Pending {tier}'s approval", …).
//   NOTE: "Overdue" is a list-*filter* value, never a WR status string —
//   overdue is derived from dueDate (see computeStatusBreakdown).
//
//   PM Occurrence `status` (WorkTaskOccurrenceState): "Upcoming",
//   "Unconfirmed", "Overdue", "Partially completed", "Pending assignment",
//   "Pending finalization", "Completed" (success), "Deleted", "Skipped",
//   "Retired".
//
// The sets below serve both entity types; 'cancelled'/'canceled' cover
// schedule-request-shaped rows defensively and never match WRs or PM
// occurrences.
const TERMINAL_STATUSES = new Set([
  'finalized', 'completed', 'cancelled', 'canceled',
  'declined', 'deleted', 'skipped', 'retired',
]);
const COMPLETED_STATUSES = new Set([
  'finalized', 'completed',
]);

function statusKey(label) {
  return typeof label === 'string' ? label.trim().toLowerCase() : null;
}

function statusLabel(wo) {
  if (!wo) return null;
  // FMX returns `status` as a plain string on WRs and PM occurrences (the
  // OpenAPI explicitly types it as `string`). `workflowStatus` is an
  // orthogonal overlay ('Active', 'On hold', 'Blocked') and does NOT
  // indicate closure — we ignore it for terminal-state checks.
  if (typeof wo.status === 'string') return wo.status;
  return wo.status?.name || wo.statusName || null;
}

export function woIsClosed(wo) {
  if (woCompletedMs(wo) != null) return true;
  const k = statusKey(statusLabel(wo));
  return k != null && TERMINAL_STATUSES.has(k);
}

// Successful completion only — used by completion-rate. Declined / Deleted /
// Skipped / Retired are terminal but not successful, so they don't count.
export function woIsCompleted(wo) {
  if (woCompletedMs(wo) != null) return true;
  const k = statusKey(statusLabel(wo));
  return k != null && COMPLETED_STATUSES.has(k);
}

export function computeCompletion(inPeriod) {
  const total = inPeriod.length;
  if (total === 0) return null;
  // If no row has a completion timestamp AND no recognizable status, we
  // probably don't have the right fields — bail rather than report 0%.
  const completedKnown = inPeriod.some(wo => woCompletedMs(wo) != null || wo?.status);
  if (!completedKnown) return null;
  // Completion = successful close. For WRs that's 'Finalized'; for PMs
  // 'Completed'. Declined/Deleted/Skipped/Retired are terminal but
  // unsuccessful and don't count as completion.
  const completed = inPeriod.filter(woIsCompleted).length;
  return { completed, total };
}

// Returns median + mean + mode for THREE timing series:
//
//   response   — create → first 'response' action (a user comment),
//                falling back to the resolution timestamp when the
//                request was resolved with no comment. This is what the
//                rubric grades against.
//   assignment — create → first assignment (assignedTimeUtc). Auxiliary
//                signal so the team can see how quickly tickets are
//                getting picked up, distinct from when they're answered.
//   resolution — create → final resolution (resolutionTimeUtc). When
//                the work was actually finished.
//
// `actionsByWrId` is an optional Map<wrId, Action[]>. When absent or
// empty for a given WR, the function falls back to the resolution
// timestamp for the "response" series — the user explicitly asked for
// "first response, OR when the resolution occurs".
//
// Mode is bucketed by whole days because exact-millisecond deltas would
// never repeat.
export function computeResponseTime(inPeriod, actionsByWrId) {
  const responseDeltas   = [];
  const assignmentDeltas = [];
  const resolutionDeltas = [];

  for (const wo of inPeriod) {
    const created = woCreatedMs(wo);
    if (created == null) continue;

    const firstRsp = firstResponseMs(wo, actionsByWrId?.get(wo?.id));
    if (firstRsp != null && firstRsp >= created) {
      responseDeltas.push((firstRsp - created) / DAY_MS);
    }

    const assigned = woTimestampMs(wo, ['assignedTimeUtc']);
    if (assigned != null && assigned >= created) {
      assignmentDeltas.push((assigned - created) / DAY_MS);
    }

    const resolved = woCompletedMs(wo);
    if (resolved != null && resolved >= created) {
      resolutionDeltas.push((resolved - created) / DAY_MS);
    }
  }

  if (responseDeltas.length === 0 && assignmentDeltas.length === 0 && resolutionDeltas.length === 0) {
    return null;
  }
  const r = describeDeltas(responseDeltas);
  const a = describeDeltas(assignmentDeltas);
  const x = describeDeltas(resolutionDeltas);
  return {
    // Response — graded.
    responseDays:       r.median, responseMeanDays:   r.mean, responseModeDays:   r.mode, responseN:   r.N,
    // Assignment — auxiliary, not graded but kept so the FMXer can see
    // how quickly things are being picked up.
    assignmentDays:     a.median, assignmentMeanDays: a.mean, assignmentModeDays: a.mode, assignmentN: a.N,
    // Resolution — graded.
    resolutionDays:     x.median, resolutionMeanDays: x.mean, resolutionModeDays: x.mode, resolutionN: x.N,
  };
}

// First-response time per the user definition: earliest response action
// (a comment left on the request), OR the resolution timestamp if the
// request was resolved without a comment, whichever is earlier.
//
// Action `kind` is the model class name — "WorkRequestResponse",
// "WorkRequestResolution", "WorkRequestReopening", etc. (verified in
// WorkRequestActionModelMapper). Matched case-insensitively; the bare
// 'response' form is accepted defensively for any tenant/version that
// serializes a short kind.
function firstResponseMs(wo, actions) {
  let earliest = null;
  if (Array.isArray(actions)) {
    for (const a of actions) {
      const k = typeof a?.kind === 'string' ? a.kind.toLowerCase() : '';
      if (k !== 'workrequestresponse' && k !== 'response') continue;
      if (!a.createdTimeUtc) continue;
      const t = Date.parse(a.createdTimeUtc);
      if (Number.isNaN(t)) continue;
      if (earliest == null || t < earliest) earliest = t;
    }
  }
  const resolved = woCompletedMs(wo);
  if (resolved != null && (earliest == null || resolved < earliest)) {
    earliest = resolved;
  }
  return earliest;
}

function describeDeltas(deltas) {
  if (deltas.length === 0) {
    return { median: null, mean: null, mode: null, N: 0 };
  }
  return {
    median: round1(median(deltas)),
    mean:   round1(mean(deltas)),
    mode:   modeByDay(deltas),
    N:      deltas.length,
  };
}

function mean(nums) {
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function modeByDay(nums) {
  // Bucket each delta into its floor-day, count the buckets, return the
  // most-common bucket. Ties go to the smaller day (stable across rerun).
  const counts = new Map();
  for (const n of nums) {
    const day = Math.floor(n);
    counts.set(day, (counts.get(day) || 0) + 1);
  }
  let best = null, bestCount = -1;
  for (const [day, count] of counts) {
    if (count > bestCount || (count === bestCount && day < best)) {
      best = day; bestCount = count;
    }
  }
  return best;
}

// ----- Slide-specific aggregations (no rubric impact) -----

// Buckets the FMX-documented WR statuses into the 4 columns the deck's
// "By Status" table shows on slide 7. Returns counts AND % of the
// in-period total.
//   Completed  = Finalized OR Pending finalization. Mirrors FMX's own
//                "Completed" filter {PendingFinalization, Finalized}
//                (WorkRequestStates.cs) AND the completion_rate formula
//                (woIsCompleted treats a resolution timestamp as
//                complete) — so the table agrees with the headline
//                completion % on the same slide.
//   In progress = Pending resolution / Pending assignment
//   Pending     = Pending details / approval statuses
//   Overdue     = open + past dueDate
// "Overdue" is derived from dueDate (past due + still open) because FMX
// never emits "Overdue" as a WR status string — it's a filter-only
// value. An overdue-but-open WR is counted once, in the overdue bucket.
// Dynamic approval statuses ("Pending {tier}'s approval", …) fall into
// the pending bucket via the prefix match rather than disappearing into
// `other`.
export function computeStatusBreakdown(inPeriod, asOfMs = Date.now()) {
  if (inPeriod.length === 0) return null;
  const buckets = { completed: 0, inProgress: 0, pending: 0, overdue: 0, other: 0 };
  for (const wo of inPeriod) {
    const s = statusKey(statusLabel(wo));
    const due = woDueMs(wo);
    const isOverdue = isPastDueMs(due, asOfMs) && !woIsClosed(wo);
    if (s === 'finalized'
          || s === 'pending finalization')           buckets.completed++;
    else if (isOverdue)                              buckets.overdue++;
    else if (s === 'pending resolution'
          || s === 'pending assignment')             buckets.inProgress++;
    else if (s === 'pending details')                buckets.pending++;
    else if (s != null && s.startsWith('pending'))   buckets.pending++;
    else                                              buckets.other++;
  }
  const total = inPeriod.length;
  const pct = c => total > 0 ? round1((c / total) * 100) : 0;
  return {
    completedCount:  buckets.completed,  completedPct:  pct(buckets.completed),
    inProgressCount: buckets.inProgress, inProgressPct: pct(buckets.inProgress),
    pendingCount:    buckets.pending,    pendingPct:    pct(buckets.pending),
    overdueCount:    buckets.overdue,    overduePct:    pct(buckets.overdue),
    total,
  };
}

// Generic top-N-by-group helper. `keyFn(row)` returns the grouping key
// (a buildingID, a userID, an equipmentItemID array etc.) and we sort
// groups descending by count, take the first n. If `lookup` is a Map,
// the result is enriched with the looked-up `name`.
function topByGroup(rows, keyFn, n = 5, lookup = null) {
  const counts = new Map();
  for (const r of rows) {
    const k = keyFn(r);
    if (k == null) continue;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const sorted = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
  return sorted.map(([id, count]) => {
    const looked = lookup?.get?.(id);
    return looked ? { id, name: looked.name, count } : { id, count };
  });
}

// Distribution of OPEN work-request ages, bucketed for slide 12. Fed
// from the statuses=open pull (all open WRs, any age) with asOfMs = now.
// Open = !woIsClosed. Age = asOfMs - createdTimeUtc in days.
function computeAgeBuckets(all, asOfMs) {
  const buckets = { d0to7: 0, d8to14: 0, d15to30: 0, dOver30: 0 };
  for (const wo of all) {
    if (woIsClosed(wo)) continue;
    const c = woCreatedMs(wo);
    if (c == null) continue;
    const days = (asOfMs - c) / (24 * 3600 * 1000);
    if (days <= 7)         buckets.d0to7++;
    else if (days <= 14)   buckets.d8to14++;
    else if (days <= 30)   buckets.d15to30++;
    else                   buckets.dOver30++;
  }
  return buckets;
}

// Top N overdue PM occurrences grouped by task name. Each entry is
// { taskName, count, buildingName? }. PM occurrences expose `taskName`
// directly and `buildingIDs` as an array; we use the first building
// when looking up a name.
// Top equipment by WR volume (slide 14). Each WR can reference one or
// more equipment items via the equipmentItemIDs array — we count once
// per (WR, equipmentId) pair, so a WR touching 2 pieces of equipment
// contributes to both rows. Sorted desc, top n. Names + location come
// from the equipment lookup; if a piece of equipment doesn't appear in
// the lookup (filtered out, archived, etc.) we fall back to the ID.
function computeTopEquipment(woInPeriod, equipmentLookup, buildingLookup, n = 5) {
  const counts = new Map();
  for (const wo of woInPeriod) {
    const ids = Array.isArray(wo.equipmentItemIDs) ? wo.equipmentItemIDs : [];
    for (const id of ids) {
      if (id == null) continue;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([id, count]) => {
      const eq = equipmentLookup?.get?.(id);
      const buildingName = eq?.buildingID != null
        ? (buildingLookup?.get?.(eq.buildingID)?.name || null)
        : null;
      return {
        id,
        name: eq?.name || `Equipment ${id}`,
        locationName: buildingName,
        count,
      };
    });
}

function computeTopOverduePms(pmAll, buildingLookup, n = 3) {
  const overdues = pmAll.filter(p => statusKey(statusLabel(p)) === 'overdue');
  const byTask = new Map();
  for (const p of overdues) {
    const name = p.taskName || `Task ${p.taskID ?? '?'}`;
    const bId  = Array.isArray(p.buildingIDs) ? p.buildingIDs[0] : p.buildingID;
    const key  = name;
    const prev = byTask.get(key) || { taskName: name, count: 0, buildingID: bId };
    prev.count++;
    if (prev.buildingID == null && bId != null) prev.buildingID = bId;
    byTask.set(key, prev);
  }
  return [...byTask.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, n)
    .map(t => ({
      ...t,
      buildingName: t.buildingID != null
        ? (buildingLookup?.get(t.buildingID)?.name || `Building ${t.buildingID}`)
        : null,
    }));
}

// Open-backlog health from a set of open WRs (the statuses=open pull —
// see fetchOpenWorkRequests — so pre-quarter backlog is included).
// Snapshot semantics: statuses are current-state, so asOfMs should be
// "now". overdue30d = past due by more than 30 full days, where "past
// due" starts the day AFTER the calendar due date (FMX parity — see
// isPastDueMs).
export function computeBacklog(all, asOfMs) {
  const open = all.filter(wo => !woIsClosed(wo));
  if (open.length === 0) {
    return { overdue30dPct: 0, oldestDays: 0, openCount: 0, overdue30dCount: 0 };
  }
  const overdue30d = open.filter(wo => {
    const due = woDueMs(wo);
    return due != null && (asOfMs - (due + DAY_MS)) > 30 * DAY_MS;
  }).length;
  const ages = open.map(wo => {
    const c = woCreatedMs(wo);
    return c != null ? (asOfMs - c) / (24 * 3600 * 1000) : null;
  }).filter(a => a != null);
  return {
    overdue30dPct:    round1((overdue30d / open.length) * 100),
    oldestDays:       ages.length ? Math.round(Math.max(...ages)) : null,
    openCount:        open.length,
    overdue30dCount:  overdue30d,
  };
}

function median(nums) {
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

// Report which expected fields actually showed up — used by the UI to
// explain why a metric couldn't be auto-filled.
function inspectFields(sample) {
  if (!sample) return { hasCreated: false, hasCompleted: false, hasAssignment: false, hasDue: false, hasStatus: false };
  return {
    hasCreated:    woCreatedMs(sample) != null,
    hasCompleted:  woCompletedMs(sample) != null || !!sample.status,
    hasAssignment: !!sample.assignedTimeUtc,
    hasDue:        woDueMs(sample) != null,
    hasStatus:     !!sample.status || !!sample.statusName,
  };
}
